import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { BusinessTypeTable } from "@/components/business-types/BusinessTypeTable";
import { BusinessTypeDialog, BusinessType } from "@/components/business-types/BusinessTypeDialog";
import { PageTitle } from "@/components/ui/PageTitle";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const BusinessTypes = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<BusinessType | null>(null);

  const { data: businessTypes, refetch } = useQuery({
    queryKey: ["businessTypes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_types")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching business types:", error);
        throw error;
      }

      return data.map(type => ({
        ...type,
        features: typeof type.features === 'string' 
          ? JSON.parse(type.features) 
          : type.features || {
              stock: true,
              budget: true,
              history: true,
              returns: true,
              counting: true,
              settings: true,
              poultry: false
            }
      })) as BusinessType[];
    },
  });

  const handleEdit = (type: BusinessType) => {
    setSelectedType(type);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("business_types")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Type d'entreprise supprimé avec succès");
      refetch();
    } catch (error) {
      console.error("Error deleting business type:", error);
      toast.error("Erreur lors de la suppression du type d'entreprise");
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setIsDialogOpen(false);
  };

  const handleSave = async (formData: any) => {
    try {
      if (selectedType) {
        const { error } = await supabase
          .from("business_types")
          .update(formData)
          .eq("id", selectedType.id);

        if (error) throw error;

        toast.success("Type d'entreprise mis à jour avec succès");
      } else {
        const { error } = await supabase
          .from("business_types")
          .insert([formData]);

        if (error) throw error;

        toast.success("Type d'entreprise créé avec succès");
      }

      handleClose();
      refetch();
    } catch (error) {
      console.error("Error saving business type:", error);
      toast.error("Erreur lors de l'enregistrement du type d'entreprise");
    }
  };

  return (
    <DashboardLayout userType="admin">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <PageTitle
            title="Types d'entreprise"
            subtitle="Gérez les différents types d'entreprise"
          />
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un type
          </Button>
        </div>

        <BusinessTypeTable
          businessTypes={businessTypes || []}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        <BusinessTypeDialog
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          selectedType={selectedType}
          onClose={handleClose}
          onSave={handleSave}
        />
      </div>
    </DashboardLayout>
  );
};

export default BusinessTypes;